import { ConsentCheckResponse } from './../../Models/ResponseModels/Health/index'
import { createApi } from '@reduxjs/toolkit/query/react'
import { API_ENDPOINTS } from 'Constants'
import {
  FDCreateCeleryResponse,
  FDDetailsResponse,
  InsuranceQuoteResponse,
  PaymentDetailsResponse,
  ProposalFDResponse,
  QuoteDetailsResponse,
  QuoteResponse,
  Tasks,
} from 'Models/ResponseModels/Group'
import { groupQuery } from 'Services/baseQuery'
import {
  ConsentRequestData,
  ICeleryTaskRequest,
  NomineeDetailsRequest,
} from 'Models/RequestModels/Group'
import { GroupSPPCreateCustomerRequest } from 'Models/RequestModels/Group/groupfd'
import { InsurerListingResponse } from 'Models/ResponseModels/Group/fd-api'
import { IMedicalQuestionsGroup } from 'Constants/groupTypes'
import { useAppSelector } from 'app/hooks'
import { store } from 'app/store'

export const groupHealthAPI = createApi({
  reducerPath: 'groupHealthAPI',
  baseQuery: groupQuery,
  endpoints: (builder) => ({
    // create quote id is same as fd
    // create customer
    createCustomer: builder.mutation<
      { quote_id: string },
      { payload: GroupSPPCreateCustomerRequest; product: 'SPP' | 'SB' }
    >({
      query: ({ payload, product }) => {
        const endpointMap = {
          SPP: API_ENDPOINTS.BOB.CREATE_CUSTOMER_GROUPSPP,
          SB: API_ENDPOINTS.BOB.CREATE_CUSTOMER_GROUPSB,
        }

        return {
          url: endpointMap[product],

          method: 'POST',
          body: payload,
        }
      },
    }),
    // create celery tasks
    createCeleryTask: builder.mutation<
      { data: FDCreateCeleryResponse },
      ICeleryTaskRequest & { product: 'SPP' | 'SB' }
    >({
      query: ({ product, ...payload }) => {
        const endpointMap = {
          SPP: API_ENDPOINTS.BOB.CREATE_CELERY_TASK_GROUPSPP,
          SB: API_ENDPOINTS.BOB.CREATE_CELERY_TASK_GROUPSB,
        }

        return {
          url: endpointMap[product],

          method: 'POST',
          body: payload,
        }
      },
    }),

    // get quotations
    getQuotations: builder.query<QuoteDetailsResponse, { quote_id: string; product: 'SPP' | 'SB' }>(
      {
        query: ({ quote_id, product }) => {
          const endpointMap = {
            SPP: API_ENDPOINTS.BOB.GET_GROUPSPP_QUOTATIONS_SPP,
            SB: API_ENDPOINTS.BOB.GET_GROUPSPP_QUOTATIONS_SB,
          }
          return {
            url: endpointMap[product],
            method: 'GET',
            params: { quote_id },
          }
        },
      },
    ),

    // create proposal
    createProposal: builder.mutation<
      ProposalFDResponse,
      NomineeDetailsRequest & { product: 'SPP' | 'SB' }
    >({
      query: ({ product, ...payload }) => {
        const endpointMap = {
          SPP: API_ENDPOINTS.BOB.CREATE_PROPOSALS_GROUPSPP,
          SB: API_ENDPOINTS.BOB.CREATE_PROPOSALS_GROUPSB,
        }
        return {
          url: endpointMap[product],
          method: 'POST',
          body: payload,
        }
      },
    }),

    //  check data push
    checkDataPush: builder.query<any, { quote_id: string; product?: 'SPP' | 'SB' }>({
      query: ({ product = 'SPP', ...params }) => {
        const endpointMap: Record<'SPP' | 'SB', string> = {
          SPP: API_ENDPOINTS.BOB.GROUP_SPP_CHECK_DATA_PUSH,
          SB: API_ENDPOINTS.BOB.GROUP_SB_CHECK_DATA_PUSH,
        }
        return {
          url: endpointMap[product],
          method: 'GET',
          params: params,
        }
      },
    }),

    //  check data push
    getApplicationStatus: builder.query<any, { proposal_number: string; product?: 'SPP' | 'SB' }>({
      query: ({ product = 'SPP', ...params }) => {
        // const endpointMap: Record<'SPP' | 'SB', string> = {
        //   SPP: API_ENDPOINTS.BOB.GET_APPLICATION_STATUS_SPP,
        //   SB: API_ENDPOINTS.BOB.GET_APPLICATION_STATUS_SB,
        // }
        return {
          url: API_ENDPOINTS.BOB.GET_APPLICATION_STATUS_SPP,
          method: 'GET',
          params: params,
        }
      },
    }),

    // get relationships
    getRelationships: builder.query<any, { insurance_code: string }>({
      query: ({ insurance_code }) => ({
        url: API_ENDPOINTS.BOB.GET_RELATIONSHIP,
        method: 'GET',
        params: { insurance_code },
      }),
    }),

    // get occupations
    getOccupations: builder.query<any, void>({
      query: () => ({
        url: API_ENDPOINTS.BOB.GET_OCCUPATIONS,
        method: 'GET',
      }),
    }),

    // get genders
    getGenders: builder.query<any, void>({
      query: () => ({
        url: API_ENDPOINTS.BOB.GET_GENDERS,
        method: 'GET',
      }),
    }),
    // get salutations
    getSalutations: builder.query<any, void>({
      query: () => ({
        url: API_ENDPOINTS.BOB.GET_SALUTATIONS,
        method: 'GET',
      }),
    }),

    // get payment status
    getPaymentStatus: builder.query<
      PaymentDetailsResponse,
      { quote_id: string; consent_id: string }
    >({
      query: ({ quote_id, consent_id }) => ({
        url: `${API_ENDPOINTS.BOB.GET_PAYMENT_STATUS}/${quote_id}`,
        method: 'GET',
        params: { consent_id },
      }),
    }),

    // create Consent
    createConsent: builder.mutation<any, any>({
      query: (payload) => ({
        url: API_ENDPOINTS.BOB.CREATE_CONSENT,
        method: 'POST',
        body: payload,
      }),
    }),

    // get particular quote data
    getQuoteData: builder.query<QuoteResponse[], { code: string }>({
      query: ({ code }) => ({
        url: `${API_ENDPOINTS.BOB.GET_INSURANCE_DATA}`,
        method: 'GET',
        params: { code },
      }),
    }),
    getPlanInfo: builder.query<any, { insurance_code: string }>({
      query: ({ insurance_code }) => ({
        url: `${API_ENDPOINTS.BOB.GET_GROUP_PLAN_INFO}`,
        method: 'GET',
        params: { insurance_code },
      }),
    }),
    getInsurerListing: builder.mutation<InsurerListingResponse, any & { product: 'SPP' | 'SB' }>({
      query: ({ product, ...body }) => ({
        url: API_ENDPOINTS.BOB.GET_INSURER_LISTING_SPP,
        method: 'POST',
        body: body,
        rawResponse: true,
      }),
    }),
    // get medical questions
    getMedicalQuestions: builder.query<
      IMedicalQuestionsGroup,
      {
        insurer_code: string
      }
    >({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.MEDICAL_QUESTIONS,
        method: 'GET',
        params: params,
        // headers: {
        //   "Content-Type": "application/json",
        //   Authorization: "",
        // },
      }),
    }),

    // GROUP CONSENT API
    createConsentGroup: builder.mutation<any, any>({
      query: (payload) => ({
        url: `${API_ENDPOINTS.BOB.CREATE_CONSENT_GROUP}`,
        method: 'POST',
        body: payload,
      }),
    }),

    updateConsentGroup: builder.mutation<any, any>({
      query: (payload) => ({
        url: `${API_ENDPOINTS.BOB.UPDATE_CONSENT_GROUP}`,
        method: 'POST',
        body: payload,
      }),
    }),

    customerConsentStatus: builder.query<any, any>({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.CUSTOMER_CONSENT_STATUS,
        method: 'GET',
        params,
      }),
      // optional but recommended
      // keepUnusedDataFor: 0,
    }),
    getGenericSumInsured: builder.query<string[], any>({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.GROUP_HEALTH_SUMINSURED_GENRIC,
        method: 'GET',
        params: params,
        // headers: getHealthHeaders(),
      }),
    }),
    checkGroupPayment: builder.query<any, any>({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.PAYMENT_CHECK_GROUP,
        method: 'GET',
        params,
      }),
    }),
    getGroupPolicyPdf: builder.query<any, { quote_id: string }>({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.GET_GROUP_POLICY_PDF,
        method: 'GET',
        params,
      }),
    }),
  }),
})

export const {
  useCreateCustomerMutation,
  useCreateCeleryTaskMutation,
  useLazyGetQuotationsQuery,
  useCreateProposalMutation,
  useGetRelationshipsQuery,
  useGetOccupationsQuery,
  useGetGendersQuery,
  useGetSalutationsQuery,
  useLazyGetPaymentStatusQuery,
  useLazyGetQuoteDataQuery,
  useCreateConsentMutation,
  useLazyGetMedicalQuestionsQuery,
  useGetGenericSumInsuredQuery,
  useLazyGetPlanInfoQuery,
  useGetInsurerListingMutation,
  useCreateConsentGroupMutation,
  useUpdateConsentGroupMutation,
  useCustomerConsentStatusQuery,
  useLazyCustomerConsentStatusQuery,
  useCheckGroupPaymentQuery,
  useLazyCheckGroupPaymentQuery,
  useLazyCheckDataPushQuery,
  useLazyGetApplicationStatusQuery,
  useLazyGetGroupPolicyPdfQuery,
  useGetGroupPolicyPdfQuery,
} = groupHealthAPI
